import React, { useRef, useState } from "react";
import { Upload, FileSpreadsheet, CheckCircle, AlertTriangle, Download } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api, errorMessage } from "@/lib/api";

export default function BulkUploadPage() {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [drag, setDrag] = useState(false);

  const handleFile = (f) => {
    setResult(null); setError("");
    if (!f) return;
    const lower = f.name.toLowerCase();
    if (!lower.endsWith(".csv") && !lower.endsWith(".xlsx") && !lower.endsWith(".xls")) {
      setError("Only .csv and .xlsx files are supported.");
      return;
    }
    setFile(f);
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/users/bulk-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
    } catch (e) { setError(errorMessage(e)); }
    finally { setUploading(false); }
  };

  const downloadTemplate = () => {
    const csv = "name,email,modules,mobile,organization\nJane Doe,jane@example.com,salesforce,9991112222,Acme Corp\nJohn Smith,john@example.com,\"servicenow,salesforce\",,Steadfast\nPriya P,priya@example.com,both,,Delta University\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "steadfast_bulk_users_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AppShell section="admin">
      <div className="p-6 lg:p-10 max-w-4xl">
        <div className="overline mb-3">Admin Console</div>
        <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tight mb-2">Bulk upload users</h1>
        <p className="text-[14px] text-muted-foreground mb-8">
          Upload a CSV or XLSX roster. Users are created as <strong className="text-foreground">Active</strong>, modules are auto-assigned, and a welcome email with temporary credentials is sent.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-border bg-white mb-8">
          <div className="p-5 md:border-r border-border">
            <div className="overline mb-2">Required</div>
            <div className="font-display font-bold text-[15px]">name, email</div>
          </div>
          <div className="p-5 md:border-r border-border border-t md:border-t-0">
            <div className="overline mb-2">Optional</div>
            <div className="font-display font-bold text-[15px]">modules, mobile, organization</div>
          </div>
          <div className="p-5 border-t md:border-t-0">
            <div className="overline mb-2">Modules column values</div>
            <div className="font-display font-bold text-[15px]">servicenow · salesforce · both</div>
          </div>
        </div>

        <div className="flex items-center gap-3 mb-6">
          <button data-testid="template-download" onClick={downloadTemplate} className="h-10 px-4 inline-flex items-center gap-2 border border-border font-semibold text-[13px] hover:bg-accent">
            <Download className="h-3.5 w-3.5" /> Download CSV template
          </button>
        </div>

        <label
          onDragEnter={(e) => { e.preventDefault(); setDrag(true); }}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files?.[0]); }}
          className={`block border-2 border-dashed p-12 text-center cursor-pointer transition-colors ${drag ? "border-primary bg-primary/5" : "border-border bg-accent/40"}`}
          data-testid="bulk-dropzone"
        >
          <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="bulk-file-input" />
          <Upload className="h-8 w-8 mx-auto text-muted-foreground" aria-hidden="true" />
          <div className="font-display font-bold mt-3">Drop your file here, or click to browse</div>
          <div className="text-[12.5px] text-muted-foreground mt-1">CSV, XLS, or XLSX. Up to 10,000 rows.</div>
          {file && (
            <div className="mt-5 inline-flex items-center gap-2 px-3 py-2 border border-border bg-white">
              <FileSpreadsheet className="h-4 w-4 text-primary" />
              <span className="font-semibold text-[13px]">{file.name}</span>
              <span className="text-muted-foreground text-[12px]">{(file.size / 1024).toFixed(1)} KB</span>
            </div>
          )}
        </label>

        <div className="mt-6 flex items-center gap-2">
          <button data-testid="bulk-upload-button" onClick={upload} disabled={!file || uploading} className="h-11 px-6 bg-primary text-primary-foreground font-semibold text-[13px] disabled:opacity-50 hover:bg-[#001d6b]">
            {uploading ? "Uploading..." : "Upload & create accounts"}
          </button>
          {file && (
            <button onClick={() => { setFile(null); setResult(null); if (inputRef.current) inputRef.current.value = ""; }} className="h-11 px-5 border border-border font-semibold text-[13px]">
              Clear
            </button>
          )}
        </div>

        {error && <div className="mt-6 p-3 border border-destructive/30 bg-destructive/5 text-destructive text-[13px]" data-testid="bulk-error">{error}</div>}

        {result && (
          <div className="mt-8 border border-border bg-white" data-testid="bulk-result">
            <div className="grid grid-cols-3 border-b border-border">
              <div className="p-5 border-r border-border">
                <div className="overline mb-2">Created</div>
                <div className="font-display font-black text-3xl text-primary">{result.created}</div>
              </div>
              <div className="p-5 border-r border-border">
                <div className="overline mb-2">Skipped</div>
                <div className="font-display font-black text-3xl">{result.skipped}</div>
              </div>
              <div className="p-5">
                <div className="overline mb-2">Errors</div>
                <div className="font-display font-black text-3xl text-destructive">{result.errors}</div>
              </div>
            </div>
            {(result.details?.errors?.length > 0 || result.details?.skipped?.length > 0) && (
              <div className="p-5 space-y-3">
                {result.details.errors?.length > 0 && (
                  <div>
                    <div className="overline mb-2 flex items-center gap-2"><AlertTriangle className="h-3.5 w-3.5 text-destructive" /> Errors</div>
                    <ul className="text-[13px] text-muted-foreground space-y-1">
                      {result.details.errors.map((er, i) => (
                        <li key={i}>Row {er.row} {er.email ? `(${er.email})` : ""} — {er.error}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {result.details.skipped?.length > 0 && (
                  <div>
                    <div className="overline mb-2">Skipped</div>
                    <ul className="text-[13px] text-muted-foreground space-y-1">
                      {result.details.skipped.map((s, i) => (<li key={i}>Row {s.row} — {s.email} ({s.reason})</li>))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            {result.created > 0 && (
              <div className="p-5 border-t border-border text-[13px] text-primary inline-flex items-center gap-2">
                <CheckCircle className="h-4 w-4" /> Welcome emails with temporary credentials have been sent to all newly created users.
              </div>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}
